

import { PrintDataFields } from "@repo/types";
import { imageService } from "@repo/ui/lib/services";
import { useUserSettings } from "../../../../../hooks/api/user-settings";
import { formatDate } from "../../../epaysage/print/utils/format-utils";
import { AccessMap } from "./AccessMap";

export interface InterventionFormProps {
  printData: PrintDataFields;
  inventaireId: number;
}

export default function InterventionForm({ printData, inventaireId }: InterventionFormProps) {
  const { data: userSettingsData } = useUserSettings();

  const handlePrint = () => {
    window.print();
  };

  // Parse GPS coordinates from print-data format (string "lat, lng")
  const parseGPS = (gps: string): { lat: number; lng: number } | undefined => {
    if (!gps) return undefined;
    const parts = gps.split(',').map(p => p.trim()).filter(p => p.length > 0);
    if (parts.length >= 2) {
      const latStr: string | undefined = parts[0];
      const lngStr: string | undefined = parts[1];
      if (latStr !== undefined && lngStr !== undefined) {
        const lat = parseFloat(latStr);
        const lng = parseFloat(lngStr);
        if (!isNaN(lat) && !isNaN(lng)) {
          return { lat, lng };
        }
      }
    }
    return undefined;
  };

  // Extract arbre ID from headerTitle format "Arbre – {id}"
  const extractArbreId = (headerTitle: string): number | null => {
    const match = headerTitle.match(/Arbre\s*–\s*(\d+)/);
    if (match && match[1]) {
      const id = parseInt(match[1], 10);
      return isNaN(id) ? null : id;
    }
    return null;
  };

  // Format dates
  const defaultStartTime = "08:30";
  const defaultEndTime = "17:00";
  const travauxDateValue = printData.travaux?.date_travaux || "";
  const interventionDate = travauxDateValue
  const travauxDate = travauxDateValue
  // Get company information
  const organizationLogo = printData.organizationLogo
    ? imageService.getImageUrl(printData.organizationLogo, 'logo')
    : null;

  // Parse GPS coordinates
  const coord = parseGPS(printData.gps);
  const gpsCoords = coord ? `${coord.lat}, ${coord.lng}` : "";

  // Extract data - use print-data field names directly
  const interventionNumber = inventaireId || printData.code_site || "";
  const clientName = printData.code_site || "";
  const addressLine = printData.addressLine || "";
  const rootCollet = printData.defautsMecaniques?.collet || "";
  const trunk = printData.defautsMecaniques?.tronc || "";
  const houppier = printData.defautsMecaniques?.houppier || "";
  const comments = printData.comments || "";
  const travauxRootCollet = printData.travaux?.travaux_collet || "";
  const travauxTrunk = printData.travaux?.travaux_tronc || "";
  const travauxHouppier = printData.travaux?.travaux_houppier || "";
  const remarks = printData.environnement?.proximite || "";
  const nuisance = printData.environnement?.nuisance || "";
  const intervenersList = userSettingsData?.data?.list_intervenant || "";
  const materielList = userSettingsData?.data?.list_materiel || [];

  // Parse matériel list - it's now an array of objects
  const materielArray = Array.isArray(materielList) ? materielList : [];
  // Parse intervenants list into array
  const intervenersArray = intervenersList ? intervenersList.split('\n').filter(line => line.trim()) : [];

  return (
    <div className="min-h-screen bg-white p-8 print:p-4">

      {/* Page Content */}
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="border border-gray-300 p-4 w-[200px] h-[200px] flex items-center justify-center">
            {organizationLogo ? (
              <img src={organizationLogo} alt="Logo entreprise" className="max-h-full max-w-full object-contain" />
            ) : (
              <div className="text-xs text-gray-400">Logo</div>
            )}
          </div>
          <h1 className="text-3xl font-bold text-black flex-1 text-center">FICHE D'INTERVENTION</h1>
          <div className="w-40"></div>
        </div>

        {/* Info Boxes */}
        <div className="space-y-4 mb-6">
          {/* Intervention Info */}
          <div className="border-2 border-black p-4">
            <div className="space-y-2">
              <div className="flex">
                <span className="font-bold">Numéro d'intervention :</span>
                <span className="border-b border-black flex-1 ml-2">{interventionNumber}</span>
              </div>
              <div className="flex">
                <span className="font-bold">Date et heure de début :</span>
                <span className="border-b border-black flex-1 ml-2">{interventionDate} à {defaultStartTime}</span>
              </div>
              <div className="flex">
                <span className="font-bold">Date et heure de fin :</span>
                <span className="border-b border-black flex-1 ml-2">{travauxDate} à {defaultEndTime}</span>
              </div>
            </div>
          </div>

          {/* Client Info */}
          <div className="border-2 border-black p-4">
            <div className="space-y-2">
              <div className="flex">
                <span className="font-bold">Nom du client :</span>
                <span className="border-b border-black flex-1 ml-2">{clientName}</span>
              </div>
              <div className="flex">
                <span className="font-bold">Adresse :</span>
                <span className="border-b border-black flex-1 ml-2">{addressLine}</span>
              </div>
              <div className="border-b border-black h-6"></div>
              <div className="border-b border-black h-6"></div>
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div className="border-2 border-black p-4 mb-4">
          <div className="flex justify-between">
            <div className="flex-1 pr-4">
              <div className="flex mb-2">
                <span className="font-bold underline">Description :</span>
                <span className="border-b border-black flex-1 ml-2"></span>
              </div>
              <div className="ml-4 space-y-2">
                <div className="flex">
                  <span className="mr-2">-</span>
                  <span>Système racinaire collet :</span>
                  <span className="border-b border-black flex-1 ml-2">{rootCollet}</span>
                </div>
                <div className="flex">
                  <span className="mr-2">-</span>
                  <span>Tronc :</span>
                  <span className="border-b border-black flex-1 ml-2">{trunk}</span>
                </div>
                <div className="flex">
                  <span className="mr-2">-</span>
                  <span>Houppier :</span>
                  <span className="border-b border-black flex-1 ml-2">{houppier}</span>
                </div>
              </div>
              <div className="mt-4">
                <span className="font-bold">Commentaires :</span>
                <div className="border-b border-black h-6 mt-1">{comments || ""}</div>
                <div className="border-b border-black h-6 mt-1"></div>
                <div className="border-b border-black h-6 mt-1"></div>
              </div>
            </div>

          </div>
        </div>

        {/* Travaux Section */}
        <div className="border-2 border-black p-4 mb-4">
          <div className="flex justify-between">
            <div className="flex-1 pr-4">
              <div className="flex mb-2">
                <span className="font-bold underline">Travaux :</span>
                <span className="border-b border-black flex-1 ml-2"></span>
              </div>
              <div className="space-y-2">
                <div className="flex">
                  <span>Système racinaire collet :</span>
                  <span className="border-b border-black flex-1 ml-2">{travauxRootCollet}</span>
                </div>
                <div className="flex">
                  <span>Tronc :</span>
                  <span className="border-b border-black flex-1 ml-2">{travauxTrunk}</span>
                </div>
                <div className="flex">
                  <span>Houppier :</span>
                  <span className="border-b border-black flex-1 ml-2">{travauxHouppier}</span>
                </div>
              </div>
              <div className="border-b border-black h-6 mt-2"></div>
              <div className="border-b border-black h-6 mt-1"></div>
              <div className="border-b border-black h-6 mt-1"></div>
            </div>

          </div>
        </div>

        {/* Remarques Section */}
        <div className="border-2 border-black p-4 mb-6">
          <div className="flex justify-between">
            <div className="flex-1 pr-4">
              <div className="flex mb-2">
                <span className="font-bold underline">Environnements et observations :</span>
                <span className="border-b border-black flex-1 ml-2"></span>
              </div>
              <div className="space-y-2">
                <div className="flex">
                  <span>=</span>
                  <span className="border-b border-black flex-1 ml-2">{remarks || ""}</span>
                </div>
                <div className="flex">
                  <span>+</span>
                  <span className="border-b border-black flex-1 ml-2"></span>
                </div>
                <div className="flex">
                  <span>=</span>
                  <span className="border-b border-black flex-1 ml-2">
                    {nuisance}
                  </span>
                </div>
              </div>
              <div className="border-b border-black h-6 mt-2"></div>
              <div className="border-b border-black h-6 mt-1"></div>
              <div className="border-b border-black h-6 mt-1"></div>
            </div>

          </div>
        </div>



        {/* Emergency Numbers Footer - Optional, can be added if image exists */}
        <div className="flex justify-center mb-6">
          <img src="/images/numeros-urgence-1.jpg" alt="Numéros d'urgence" className="w-full max-w-3xl" />
        </div>

        {/* Equipment Table */}
        <div className="border-2 border-black mb-6 print:page-break-after-always">
          <table className="w-full">
            <thead>
              <tr className="bg-black text-white">
                <th className="py-2 px-4 text-center font-bold border-r border-white">Equipement</th>
                <th className="py-2 px-4 text-center font-bold border-r border-white">Modèle</th>
                <th className="py-2 px-4 text-center font-bold">Type</th>
              </tr>
            </thead>
            <tbody>
              {materielArray.length > 0 ? (
                materielArray.slice(0, 8).map((item, i) => {
                  // item is now an object with equipement, modele, type
                  const materielItem = typeof item === 'object' && item !== null && 'equipement' in item
                    ? item as { equipement: string; modele: string; type: string }
                    : { equipement: "", modele: "", type: "" };
                  return (
                    <tr key={i} className="border-t border-black">
                      <td className="py-4 px-4 border-r border-black">{materielItem.equipement || ""}</td>
                      <td className="py-4 px-4 border-r border-black">{materielItem.modele || ""}</td>
                      <td className="py-4 px-4">{materielItem.type || ""}</td>
                    </tr>
                  );
                })
              ) : (
                [...Array(8)].map((_, i) => (
                  <tr key={i} className="border-t border-black">
                    <td className="py-4 px-4 border-r border-black"></td>
                    <td className="py-4 px-4 border-r border-black"></td>
                    <td className="py-4 px-4"></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Plan d'accès Section - Keep existing access map */}
        {coord && (
          <div className="border-2 border-black p-4 mb-6 print:page-break-before-auto print:page-break-inside-avoid">
            <div className="font-bold underline mb-4">Plan d'accès :</div>
            <div className="border border-gray-300 p-2 print:overflow-hidden print:relative">
              <AccessMap
                coordinates={coord}
                address={addressLine || undefined}
              />
            </div>
          </div>
        )}

        {/* Liste des intervenants */}
        <div className="border-2 border-black p-4 mb-6 min-h-[200px]">
          <div className="font-bold underline mb-4">Liste des intervenants :</div>
          <div className="space-y-3">
            {intervenersArray.length > 0 ? (
              intervenersArray.slice(0, 5).map((intervener, i) => (
                <div key={i} className="border-b border-black h-6">{intervener}</div>
              ))
            ) : (
              [...Array(5)].map((_, i) => (
                <div key={i} className="border-b border-black h-6"></div>
              ))
            )}
          </div>
        </div>


      </div>

    </div>
  );
}
